import {
  projectTypes,
  platforms,
  features,
  integrations,
  aiComplexity,
  infrastructure,
  complexityTiers,
  planningRangeMultiplier,
  ProjectTypeId,
  PlatformTypeId,
  FeatureTypeId,
  IntegrationTypeId,
  AiTypeId,
  InfrastructureTypeId,
  ComplexityTier
} from "@/data/estimatorConfig";

export interface EstimateInput {
  projectTypeId: ProjectTypeId;
  platformId: PlatformTypeId;
  featureIds: FeatureTypeId[];
  integrationIds: IntegrationTypeId[];
  aiId: AiTypeId;
  infrastructureId: InfrastructureTypeId;
}

export interface EstimateResult {
  totalPoints: number;
  complexity: ComplexityTier;
  timelineWeeks: { min: number; max: number };
  baseCost: number;
  modifiersCost: number;
  subtotal: number;
  minEstimate: number;
  maxEstimate: number;
  currency: "USD" | "INR";
  currencySymbol: "$" | "₹";
  formattedMin: string;
  formattedMax: string;
  selectedDrivers: string[];
}

export function calculateEstimate(
  input: EstimateInput,
  currency: "USD" | "INR" = "USD"
): EstimateResult {
  // 1. Find selections
  const projectType = projectTypes.find((p) => p.id === input.projectTypeId) || projectTypes[0];
  const platform = platforms.find((p) => p.id === input.platformId) || platforms[0];
  const selectedFeatures = features.filter((f) => input.featureIds.includes(f.id));
  const selectedIntegrations = integrations.filter((i) => input.integrationIds.includes(i.id));
  const ai = aiComplexity.find((a) => a.id === input.aiId) || aiComplexity[0];
  const infra = infrastructure.find((i) => i.id === input.infrastructureId) || infrastructure[0];

  // 2. Calculate points
  const totalPoints =
    projectType.points +
    platform.points +
    selectedFeatures.reduce((sum, item) => sum + item.points, 0) +
    selectedIntegrations.reduce((sum, item) => sum + item.points, 0) +
    ai.points +
    infra.points;

  // 3. Resolve complexity tier
  const complexity =
    complexityTiers.find((t) => totalPoints >= t.minPoints && totalPoints <= t.maxPoints) ||
    complexityTiers[0];

  // 4. Resolve timeline
  const timelineWeeks = complexity.timelineWeeks;

  // 5. Calculate base costs in INR benchmark
  const baseCost = projectType.baseCost;
  const modifiersCost =
    selectedFeatures.reduce((sum, item) => sum + item.modifierCost, 0) +
    selectedIntegrations.reduce((sum, item) => sum + item.modifierCost, 0) +
    ai.modifierCost +
    infra.modifierCost;

  const rawSubtotalINR = (baseCost + modifiersCost) * platform.multiplier;

  // 6. Gather scope cost drivers
  const selectedDrivers: string[] = [];
  if (platform.id === "hybrid") {
    selectedDrivers.push(`Multi-Platform Wrap (${platform.label})`);
  }
  selectedFeatures.forEach((f) => selectedDrivers.push(f.label));
  selectedIntegrations.forEach((i) => selectedDrivers.push(i.label));
  if (ai.id !== "none") {
    selectedDrivers.push(ai.label);
  }
  if (infra.id === "enterprise") {
    selectedDrivers.push(infra.label);
  }

  // 7. Localize calculation by currency
  if (currency === "USD") {
    // 1 USD ≈ 85 INR with clean 100-dollar rounding
    const rawUSD = rawSubtotalINR / 85;
    const subtotal = Math.round(rawUSD / 100) * 100;
    const minEstimate = Math.max(2500, Math.round((subtotal * planningRangeMultiplier.min) / 100) * 100);
    const maxEstimate = Math.round((subtotal * planningRangeMultiplier.max) / 100) * 100;

    return {
      totalPoints,
      complexity,
      timelineWeeks,
      baseCost: Math.round(baseCost / 85),
      modifiersCost: Math.round(modifiersCost / 85),
      subtotal,
      minEstimate,
      maxEstimate,
      currency: "USD",
      currencySymbol: "$",
      formattedMin: `$${minEstimate.toLocaleString("en-US")}`,
      formattedMax: `$${maxEstimate.toLocaleString("en-US")}`,
      selectedDrivers,
    };
  } else {
    const subtotal = Math.round(rawSubtotalINR / 1000) * 1000;
    const minEstimate = Math.round((subtotal * planningRangeMultiplier.min) / 1000) * 1000;
    const maxEstimate = Math.round((subtotal * planningRangeMultiplier.max) / 1000) * 1000;

    return {
      totalPoints,
      complexity,
      timelineWeeks,
      baseCost,
      modifiersCost,
      subtotal,
      minEstimate,
      maxEstimate,
      currency: "INR",
      currencySymbol: "₹",
      formattedMin: `₹${minEstimate.toLocaleString("en-IN")}`,
      formattedMax: `₹${maxEstimate.toLocaleString("en-IN")}`,
      selectedDrivers,
    };
  }
}

export function validateEstimateSelection(input: Partial<EstimateInput>): boolean {
  if (!input.projectTypeId || !projectTypes.some((p) => p.id === input.projectTypeId)) return false;
  if (!input.platformId || !platforms.some((p) => p.id === input.platformId)) return false;
  if (!input.aiId || !aiComplexity.some((a) => a.id === input.aiId)) return false;
  if (!input.infrastructureId || !infrastructure.some((i) => i.id === input.infrastructureId)) return false;
  
  if (input.featureIds) {
    const allValid = input.featureIds.every((fid) => features.some((f) => f.id === fid));
    if (!allValid) return false;
  }
  
  if (input.integrationIds) {
    const allValid = input.integrationIds.every((iid) => integrations.some((i) => i.id === iid));
    if (!allValid) return false;
  }
  
  return true;
}
